import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input, Button } from '../../components/ui';
import { Mail, ArrowLeft } from 'lucide-react';
import { auth } from '../../lib/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = (): boolean => {
    if (!email.trim()) {
      setErrorKey('forgotPasswordPage.validation.emailRequired');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorKey('forgotPasswordPage.validation.emailInvalid');
      return false;
    }
    setErrorKey(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrorKey(null);
    try {
      await sendPasswordResetEmail(auth, email);
      setIsSubmitted(true);
    } catch (error: any) {
      console.error('Password reset error:', error);
      let specificErrorKey = 'auth.errors.unknown';
      switch (error.code) {
        case 'auth/user-not-found':
          specificErrorKey = 'auth.errors.auth/user-not-found';
          break;
        case 'auth/invalid-email':
          specificErrorKey = 'auth.errors.auth/invalid-email';
          break;
        case 'auth/too-many-requests':
          specificErrorKey = 'auth.errors.auth/too-many-requests';
          break;
        case 'auth/network-request-failed':
          specificErrorKey = 'auth.errors.auth/network-request-failed';
          break;
      }
      setErrorKey(specificErrorKey);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/praxi-logo.png"
            alt="Praxi"
            className="h-12 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-gray-900">
            {t('forgotPasswordPage.title')}
          </h1>
          <p className="mt-2 text-gray-600">
            {!isSubmitted ? t('forgotPasswordPage.subtitle') : ''}
          </p>
        </div>

        <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {errorKey && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
                  {t(errorKey)}
                </div>
              )}

              <Input
                label={t('forgotPasswordPage.form.emailLabel')}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrorKey(null);
                }}
                error={errorKey ? t(errorKey) : undefined}
                leftIcon={<Mail className="w-5 h-5 text-gray-400" />}
                placeholder={t('forgotPasswordPage.form.emailPlaceholder')}
                required
                disabled={isLoading}
                autoComplete="email"
              />

              <Button
                variant="primary"
                className="w-full"
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {t('forgotPasswordPage.form.submitButton')}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('forgotPasswordPage.success.title')}
              </h2>
              <p className="text-gray-600">
                {t('forgotPasswordPage.success.message', { email: email })}
              </p>
            </div>
          )}
          
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('forgotPasswordPage.backLink')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;